/**
 * Defines a custom useForm hook that provides functionality for form state management,
 * field validation, and form submission. This hook abstracts the common tasks associated
 * with handling form inputs, validating them against a set of rules, resetting form fields,
 * and managing the submission process including token generation and API calls.
 *
 * @module useForm
 */

import { onSubmit, validateFields } from '../functions/forms';
import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

export const SubmissionStatus = {
    IDLE: 'idle',
    IN_PROGRESS: 'inProgress',
    SUCCESS: 'success',
    ERROR: 'error'
};

const initialSubmissionState = {
    status: SubmissionStatus.IDLE,
    message: ''
};

/**
 * Custom hook for managing form logic including state, validation, and submission.
 *
 * @param {Object} initialFormState - The initial state of the form fields
 * @param {Array<string>} requiredFields - Array of field ids that are required for the form
 * @param {Object} validationConfig - Configuration object for validating fields
 * @param {Object} submitConfig - Configuration for the form submission, including API endpoint and payload structure
 * @returns {Object} Collection of states and functions for managing form behavior
 */
export const useForm = (
    initialFormState,
    requiredFields,
    validationConfig,
    submitConfig
) => {
    const [formState, setFormState] = useState(initialFormState);
    const [submissionState, setSubmissionState] = useState(
        initialSubmissionState
    );

    const { getAccessTokenSilently } = useAuth0();

    /**
     * Resets form fields to their initial state and indicates that the form has not been successfully sent.
     */
    const resetFields = () => {
        setFormState(initialFormState);
        setSubmissionState(initialSubmissionState);
    };

    /**
     * Determines if a form field has been clicked and contains an error.
     *
     * @param {string} id - The identifier of the form field
     * @returns {boolean} True if the field has an error and has been interacted with, otherwise false
     */
    const isErrorAndClicked = (id) => {
        const field = formState[id];
        return field.error && field.clicked;
    };

    /**
     * Updates a specific form field with new properties.
     *
     * @param {string} key - The key of the form field to update
     * @param {Object} update - An object containing the properties to update in the form field
     */
    const updateFormField = (key, update) => {
        setFormState((prevState) => ({
            ...prevState,
            [key]: {
                ...prevState[key],
                ...update
            }
        }));
    };

    /**
     * Sets the value of a form field and determines if it is in error based on required validation.
     *
     * @param {string} id - The identifier of the form field
     * @param {string} value - The new value for the form field
     */
    const setFormField = (id, value) => {
        const isError = value.trim() === '';
        if (requiredFields.includes(id)) {
            setFormState({
                ...formState,
                [id]: { ...formState[id], value: value, error: isError }
            });
        } else {
            setFormState({
                ...formState,
                [id]: { ...formState[id], value: value }
            });
        }
    };

    /**
     * Sets the phone number field's value and countryCode, and resets the valid flag
     *
     * @param {string} value - The new phone number
     * @param {MuiTelInputInfo} info - An object containing information about the phone number
     */
    const setPhoneNumber = (value, info) => {
        setFormState({
            ...formState,
            phoneNumber: {
                countryCode: info.countryCallingCode,
                value: value,
                error: false,
                valid: true
            }
        });
    };

    /**
     * Marks a form field as clicked and sets its error state based on its value.
     *
     * @param {string} id - The identifier of the form field.
     */
    const setFormFieldClicked = (id) => {
        const isError = formState[id].value.trim() === '';
        setFormState({
            ...formState,
            [id]: { ...formState[id], clicked: true, error: isError }
        });
    };

    /**
     * Handles form submission, including validation and making the API call.
     */
    const handleSend = async () => {
        const allValid = validateFields(
            formState,
            updateFormField,
            validationConfig
        );

        if (allValid) {
            setSubmissionState({
                status: SubmissionStatus.IN_PROGRESS,
                message: ''
            });
            try {
                const token = await getAccessTokenSilently();

                await onSubmit(formState, submitConfig, token);

                setSubmissionState({
                    status: SubmissionStatus.SUCCESS,
                    message: ''
                });
                return true;
            } catch (submissionError) {
                setSubmissionState({
                    status: SubmissionStatus.ERROR,
                    message:
                        'Something went wrong. Please try again or come back later'
                });
                throw submissionError;
            }
        } else {
            return false;
        }
    };

    return {
        formState,
        setFormState,
        resetFields,
        setFormField,
        handleSend,
        setFormFieldClicked,
        submissionState,
        isErrorAndClicked,
        setPhoneNumber
    };
};
